import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardActionArea,
  Box,
  Chip,
  useTheme,
  Skeleton,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import CardRenderer, { getConfig } from '../components/CardRenderer';

const USE_CASES = [
  { id: 'all', label: 'All' },
  { id: 'doctors', label: 'For Doctors' },
  { id: 'freelancers', label: 'For Freelancers' },
  { id: 'real-estate', label: 'For Real Estate' },
  { id: 'students', label: 'For Students' },
  { id: 'sales-teams', label: 'For Sales Teams' },
];

const categoryToUseCase = (cat) => {
  const c = (cat || '').toLowerCase();
  if (c.includes('health')) return 'doctors';
  if (c.includes('freelance') || c.includes('creative')) return 'freelancers';
  if (c.includes('real') || c.includes('estate')) return 'real-estate';
  if (c.includes('student') || c.includes('campus')) return 'students';
  if (c.includes('sales') || c.includes('corporate')) return 'sales-teams';
  return 'all';
};

const SAMPLE_DATA = {
  'Professional Gradient': { name: 'Rahul Sharma', title: 'Product Designer', company: 'Surat, India', phone: '+91 99887 76655', email: 'rahul@design.co', website: 'rahulsharma.com' },
  'Classic Professional': { name: 'Rahul Sharma', title: 'Product Designer', company: 'Surat, India', phone: '+91 99887 76655', email: 'rahul@design.co', website: 'rahulsharma.com' },
  'Clean Minimal': { name: 'Priya Kumar', title: 'UX Researcher', company: 'Mumbai', phone: '+91 98765 43210', email: 'priya@studio.com', website: 'priyakumar.com' },
  'Modern Creative': { name: 'Aditya Verma', title: 'Creative Director', company: 'Delhi', phone: '+91 98765 11111', email: 'aditya@creative.io', website: 'adityaverma.com' },
  'Minimalist': { name: 'Arjun Mehta', title: 'Business Consultant', phone: '+91 99887 76655', email: 'arjun@consulting.io', website: 'arjunmehta.com' },
  'Executive Pro': { name: 'Neha Kapoor', title: 'Director', company: 'TechCorp India', phone: '+91 88776 55443', email: 'neha@techcorp.in', website: 'nehakapoor.com' },
  'Medical Professional': { name: 'Dr. Anita Desai', title: 'Cardiologist', company: 'Heart Care Clinic', phone: '+91 98765 00001', email: 'dr.anita@heartcare.in' },
  'Healthcare Elite': { name: 'Dr. Vikram Patel', title: 'Neurologist', company: 'Apollo Hospital', phone: '+91 98765 00002', email: 'vikram@apollo.in' },
  'Creative Portfolio': { name: 'Zara Ahmed', title: 'Photographer', phone: '+91 98765 00003', email: 'zara@portfolio.com', website: 'zaraahmed.art' },
  'Freelance Modern': { name: 'Karan Singh', title: 'Full-Stack Developer', phone: '+91 98765 00004', email: 'karan@freelance.dev', website: 'karansingh.dev' },
  'Property Expert': { name: 'Meera Joshi', title: 'Senior Broker', company: 'Prime Realty', phone: '+91 98765 00005', email: 'meera@primerealty.in' },
  'Realtor Pro': { name: 'Amit Patel', title: 'Real Estate Agent', company: 'HomeFinder', phone: '+91 98765 00006', email: 'amit@homefinder.in' },
  'Campus Connect': { name: 'Riya Sharma', title: 'Computer Science', company: 'IIT Bombay', phone: '+91 98765 00007', email: 'riya@campus.edu' },
  'Sales Executive': { name: 'Rohan Gupta', title: 'VP of Sales', company: 'SalesForce India', phone: '+91 98765 00008', email: 'rohan@salesforce.in' },
  'Business Prestige': { name: 'Vikram Malhotra', title: 'Managing Director', company: 'Sterling Capital Partners', phone: '+91 98765 00009', email: 'vikram@sterlingcp.in' },
};

const TemplateSelection = () => {
  useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editCardId = searchParams.get('edit');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [useCaseFilter, setUseCaseFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await api.get('/templates');
        if (cancelled) return;
        if (data?.success && Array.isArray(data.templates)) {
          setTemplates(
            data.templates.map((t) => ({
              id: t.id,
              name: t.name,
              description: t.description || '',
              category: t.category || 'Professional',
              slug: t.slug,
              useCase: categoryToUseCase(t.category),
            }))
          );
        } else {
          setTemplates([]);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || 'Failed to load templates');
          setTemplates([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchTemplates();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100%', py: 4 }}>
        <Container maxWidth="lg">
          <Skeleton variant="text" width={280} height={40} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={420} height={24} sx={{ mb: 4 }} />
          <Grid container spacing={2.5}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100%', py: 4 }}>
        <Container maxWidth="lg">
          <Typography color="error">{error}</Typography>
        </Container>
      </Box>
    );
  }

  const filtered = templates.filter((t) => useCaseFilter === 'all' || t.useCase === useCaseFilter);

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100%', py: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
            Choose your template
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 560, mb: 2.5 }}>
            Each template has a unique layout and style. Hover on any template and click to start.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {USE_CASES.map((uc) => (
              <Chip
                key={uc.id}
                label={uc.label}
                onClick={() => setUseCaseFilter(uc.id)}
                variant={useCaseFilter === uc.id ? 'filled' : 'outlined'}
                color={useCaseFilter === uc.id ? 'primary' : 'default'}
              />
            ))}
          </Box>
        </Box>

        <Grid container spacing={2.5}>
          {filtered.map((template) => {
            const cfg = getConfig(template.name);
            const sampleData = SAMPLE_DATA[template.name] || { name: 'Your Name', title: 'Your Title' };
            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={template.id}>
                <Card
                  variant="outlined"
                  sx={{
                    height: 340,
                    display: 'flex',
                    flexDirection: 'column',
                    borderColor: 'divider',
                    overflow: 'hidden',
                    position: 'relative',
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
                      transform: 'translateY(-3px)',
                      '& .template-overlay': { opacity: 1 },
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => navigate(`/customize?template=${template.id}${editCardId ? `&edit=${editCardId}` : ''}`)}
                    sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                  >
                    {/* Mini preview — fixed height */}
                    <Box sx={{ height: 220, flexShrink: 0, position: 'relative' }}>
                      <CardRenderer templateName={template.name} cardData={sampleData} mini />
                      {cfg.badge && (
                        <Box sx={{ position: 'absolute', top: 10, left: 10, px: 1, py: 0.25, borderRadius: '50px', bgcolor: cfg.badge === 'Popular' ? '#00A7E3' : '#00B3A1', color: 'white', fontSize: '0.65rem', fontWeight: 700, zIndex: 2 }}>
                          {cfg.badge}
                        </Box>
                      )}
                    </Box>
                    {/* Info */}
                    <Box sx={{ px: 2, py: 1.5, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }} noWrap>
                          {template.name}
                        </Typography>
                        <Chip label={template.category} size="small" sx={{ fontSize: '0.6rem', height: 18, color: 'text.secondary', borderColor: 'divider' }} variant="outlined" />
                      </Box>
                      <Typography variant="caption" sx={{ color: 'text.disabled', lineHeight: 1.3 }} noWrap>
                        {cfg.bestFor}
                      </Typography>
                    </Box>
                  </CardActionArea>
                  {/* Hover overlay with Use Template button */}
                  <Box
                    className="template-overlay"
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: 'rgba(16, 46, 92, 0.7)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1.5,
                      opacity: 0,
                      transition: 'opacity 0.25s ease',
                      zIndex: 3,
                      pointerEvents: 'none',
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700 }}>
                      {template.name}
                    </Typography>
                    <Box
                      component="button"
                      onClick={(e) => { e.stopPropagation(); navigate(`/customize?template=${template.id}${editCardId ? `&edit=${editCardId}` : ''}`); }}
                      sx={{
                        pointerEvents: 'auto',
                        px: 3,
                        py: 1,
                        borderRadius: '8px',
                        border: 'none',
                        bgcolor: '#00A7E3',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                        transition: 'background 0.2s',
                        '&:hover': { bgcolor: '#0080B8' },
                      }}
                    >
                      Use Template
                    </Box>
                    <Box
                      component="button"
                      onClick={(e) => { e.stopPropagation(); window.open(`/preview-template/${template.id}`, '_blank'); }}
                      sx={{
                        pointerEvents: 'auto',
                        px: 2.5,
                        py: 0.75,
                        borderRadius: '8px',
                        border: '1px solid rgba(255,255,255,0.5)',
                        bgcolor: 'transparent',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                      }}
                    >
                      Preview
                    </Box>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};

export default TemplateSelection;
